// Firma (kancelaria) — klient API modelu kluczy firmowych.
// Patrz docs/model-kluczy-firmowych.md, sekcja 7.
//
// GET /api/company/current (CompanyController) jest realnym, zawsze dostępnym endpointem —
// components/CompanyProvider woła go bezwarunkowo. `enabled` domyślnie false zostaje jako
// ochrona dla wołających spoza CompanyProvider (np. testów), którzy świadomie chcą zapytanie
// wstrzymać, a nie jako furtka rollout-u — ten się skończył w F1/F2.

import { axiosInstance } from "./axios";
import useCustomQuery from "hooks/useCustomQuery";
import { ICompanyCurrentResponse } from "dto/ICompany";

const queryKeys = {
  getCurrentCompany: "companyService.getCurrentCompany",
};

const getCurrentCompany = (): Promise<{ data: ICompanyCurrentResponse }> => {
  return axiosInstance.get("/company/current");
};

/**
 * Firma zalogowanego wraz z rolą i uprawnieniami.
 *
 * @param enabled domyślnie FALSE dla wołających spoza CompanyProvider (patrz komentarz pliku).
 */
const useGetCurrentCompany = (enabled: boolean = false) => {
  return useCustomQuery(
    queryKeys.getCurrentCompany,
    () => getCurrentCompany(),
    {
      enabled,
      // Przynależność do firmy zmienia zakres WSZYSTKIEGO — nie serwujemy jej ze stale cache
      // (każde zamontowanie odpytuje BE od nowa).
      staleTime: 0,
      // ...ale cacheTime ZOSTAJE: przy staleTime 0 i cacheTime 0 każdy remount startował od
      // `undefined`, więc przez okno refetchu sesja wyglądała jak konto BEZ FIRMY — pusty
      // sidebar, getCompanyId() null, zero uprawnień. Z cache stan poprzedni trwa do
      // odpowiedzi, a odświeżenie idzie w tle.
      cacheTime: 5 * 60 * 1000,
      // Jedna chwilowa awaria sieci degradowała sesję dokładnie tak jak brak firmy, bez
      // ponowienia i bez komunikatu. Ponawiamy TYLKO błędy przejściowe: 4xx (401/403 —
      // wygasła sesja) ponawiać nie ma po co, bo odpowiedź się nie zmieni.
      retry: (failureCount: number, error: unknown) => {
        const response = (
          error as {
            response?: { status?: number; data?: { httpCode?: number } };
          }
        )?.response;
        const status = response?.status ?? response?.data?.httpCode;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 3;
      },
    },
  );
};

export default {
  queryKeys,
  getCurrentCompany,
  useGetCurrentCompany,
};
